import {
  shouldShowMyApplicationsNav,
  shouldShowPlacementReportingNav,
  shouldShowUserDashboardNav,
  UserCanvasFlags,
} from "./userNavCanvas";

export type AvatarMenuItemKind = "link" | "action" | "divider" | "sectionLabel";

export interface AvatarMenuItem {
  kind: AvatarMenuItemKind;
  label?: string;
  description?: string;
  href?: string;
  onClick?: () => void;
  addAccount?: boolean;
  danger?: boolean;
  notificationActive?: boolean;
}

interface BuildAvatarMenuArgs {
  userCanvasFlags: UserCanvasFlags;
  onBeautistaConnectClick: () => void;
  onSignOutClick: () => void;
  isAdmin?: boolean;
  adminItems?: AvatarMenuItem[];
}

const nextUrl = () => process.env.REACT_APP_NEXT_URL || "";

export const getUserInitials = (firstName?: string, lastName?: string) => {
  const first = (firstName || "").trim().charAt(0).toUpperCase();
  const last = (lastName || "").trim().charAt(0).toUpperCase();
  return `${first}${last}` || "?";
};

export const getMobilePersonaSubtitle = (flags: UserCanvasFlags) => {
  if (flags.hasBusinessCanvas) {
    return "Business";
  }
  if (flags.hasSchoolCanvas) {
    return "School";
  }
  if (flags.hasStudentCanvas) {
    return "Student";
  }
  return "Beauty Professional";
};

export const buildAvatarMenuItems = ({
  userCanvasFlags,
  onBeautistaConnectClick,
  onSignOutClick,
  isAdmin = false,
  adminItems = [],
}: BuildAvatarMenuArgs): AvatarMenuItem[] => {
  const items: AvatarMenuItem[] = [];

  if (isAdmin && adminItems.length) {
    items.push(...adminItems, { kind: "divider" });
  }

  items.push(
    {
      kind: "link",
      label: "My Portfolio",
      href: `${nextUrl()}/portfolio`,
    },
    {
      kind: "link",
      label: "Home",
      href: `${nextUrl()}/home`,
    }
  );

  if (shouldShowUserDashboardNav(userCanvasFlags.hasSchoolCanvas)) {
    items.push({ kind: "link", label: "Dashboard", href: "/my-dashboard" });
  }

  if (
    shouldShowMyApplicationsNav(
      userCanvasFlags.hasSchoolCanvas,
      userCanvasFlags.hasApplications
    )
  ) {
    items.push({
      kind: "link",
      label: "My Applications",
      href: `${nextUrl()}/applications`,
    });
  }

  if (userCanvasFlags.hasBusinessCanvas) {
    items.push({
      kind: "link",
      label: "My Business",
      href: `${nextUrl()}/business`,
    });
  }

  if (shouldShowPlacementReportingNav(userCanvasFlags.hasSchoolCanvas)) {
    items.push({
      kind: "link",
      label: "Placement Reporting",
      href: `${nextUrl()}/school/placement`,
    });
  }

  items.push(
    { kind: "link", label: "Bookmarks", href: "/bookmarks" },
    { kind: "link", label: "Message", href: "/message" },
    { kind: "link", label: "Notifications", href: "/notifications" }
  );

  if (!userCanvasFlags.hasBusinessCanvas || !userCanvasFlags.hasSchoolCanvas) {
    items.push({ kind: "divider" });
    items.push({ kind: "sectionLabel", label: "Add an account" });

    if (!userCanvasFlags.hasBusinessCanvas) {
      items.push({
        kind: "link",
        label: "Add Business Account",
        description: "Post jobs, hire talent",
        href: `${nextUrl()}/business/create`,
        addAccount: true,
      });
    }

    if (!userCanvasFlags.hasSchoolCanvas) {
      items.push({
        kind: "link",
        label: "Add School Account",
        description: "Manage student placements",
        href: `${nextUrl()}/school/create`,
        addAccount: true,
      });
    }
  }

  items.push(
    { kind: "divider" },
    { kind: "link", label: "Membership", href: `${nextUrl()}/account?tab=subscription` },
    {
      kind: "action",
      label: "Community",
      onClick: onBeautistaConnectClick,
    },
    { kind: "divider" },
    { kind: "link", label: "Account Settings", href: "/settings" },
    {
      kind: "action",
      label: "Sign Out",
      onClick: onSignOutClick,
      danger: true,
    }
  );

  return items;
};

export const buildAdminMenuItems = (history: { push: (path: string) => void }): AvatarMenuItem[] => [
  { kind: "link", label: "My Users", href: `${nextUrl()}/admin/users` },
  { kind: "link", label: "Opportunity Dashboard", href: `${nextUrl()}/admin/opportunity` },
  { kind: "link", label: "Canvas Dashboard", href: `${nextUrl()}/admin/canvas` },
  { kind: "link", label: "School Dashboard", href: `${nextUrl()}/admin/school` },
  { kind: "link", label: "KPI Dashboard", href: `${nextUrl()}/admin/kpi` },
  { kind: "link", label: "External Payment", href: `${nextUrl()}/admin/external` },
  { kind: "link", label: "Class Metrics", href: `${nextUrl()}/admin/classes` },
  {
    kind: "link",
    label: "Badges",
    href: `${nextUrl()}/admin/badges`,
  },
];
